import { CsBrandKit } from '../../../common/models/content-studio.model';
import { VendorProfile } from '../../../common/hooks/use-vendor-profile-api.hook';

/**
 * The brand kit, as things that can orbit.
 *
 * The page used to be a stack of cards: one per field, each a label and a
 * value. That is a faithful rendering of the data model and a poor rendering
 * of what the kit IS — a picture of a brand, read from real evidence. A colour
 * shown as a hex string in a text input is a fact about a database; the same
 * colour as a disc among the photographs it was taken from is the brand.
 *
 * So: every field becomes one or more nodes, and the sphere shows them all at
 * once. This file is only the translation — where they sit and how they move
 * is the orbit's business, and keeping the two apart is what makes the mapping
 * testable without a DOM.
 */

export type BrandNodeKind = 'color' | 'image' | 'word' | 'text' | 'font' | 'logo';

/** Which editor a node opens. Kit fields and profile fields both appear. */
export type BrandFieldKey =
    | 'brandName' | 'primaryColors' | 'primaryFont' | 'toneOfVoice'
    | 'styleKeywords' | 'visualStyle' | 'bannedWords' | 'referenceImageUrls'
    | 'competitors' | 'markets' | 'houseRules';

export interface BrandNode {
    id: string;
    kind: BrandNodeKind;
    /** What the vendor reads on the node itself. */
    label: string;
    /** A hex for a swatch, a URL for an image. Nothing for words. */
    value?: string;
    /**
     * The whole text, when `label` had to be cut to fit on a node.
     *
     * Kept because a tooltip built from the label would repeat the truncation
     * — hovering "Confident, refined, expert —…" to be shown "Confident,
     * refined, expert —…" answers nothing.
     */
    detail?: string;
    /** The panel this opens. */
    field: BrandFieldKey;
    /**
     * Relative size, 0–1. The brand's own colours and photographs are what a
     * person recognises it by, so they carry more weight than the words we
     * wrote about them.
     */
    weight: number;
    /**
     * Nothing has been read or written for this field yet.
     *
     * Still a node, deliberately. A field with no value produces nothing to
     * orbit, so leaving it out would make exactly the fields that need
     * attention the only ones with no way to reach them — the orbit would show
     * a complete brand and hide the holes in it.
     */
    empty?: boolean;
}

/** Per field, how many values reach the sphere. The panel always shows all. */
const LIMITS: Partial<Record<BrandFieldKey, number>> = {
    primaryColors: 6,
    referenceImageUrls: 8,
    styleKeywords: 7,
    bannedWords: 4,
    competitors: 3,
    markets: 3,
};

/** Trimmed, de-duplicated, and capped. Blank entries are not values. */
const take = (values: unknown, field: BrandFieldKey): string[] => {
    const list = Array.isArray(values) ? values : [];
    const clean = list
        .map((v) => (typeof v === 'string' ? v.trim() : ''))
        .filter(Boolean);
    return [...new Set(clean)].slice(0, LIMITS[field] ?? 6);
};

/**
 * A short label. Deliberately short: a node wide enough for half a sentence
 * collides with its neighbours and with the brand at the centre, and the
 * sphere is a map rather than a document. Hovering shows the rest.
 */
const shorten = (text: string, max = 28): string => {
    const flat = text.replace(/\s+/g, ' ').trim();
    return flat.length <= max ? flat : `${flat.slice(0, max - 1)}…`;
};

export interface BrandNodeLabels {
    /** Placeholder text per field, for the ones with nothing in them yet. */
    empty: (field: BrandFieldKey) => string;
}

export function buildBrandNodes(
    kit: CsBrandKit | null,
    profile: VendorProfile,
    labels: BrandNodeLabels,
): BrandNode[] {
    const nodes: BrandNode[] = [];
    const k = kit ?? ({} as CsBrandKit);

    const blank = (field: BrandFieldKey, kind: BrandNodeKind = 'text') =>
        nodes.push({ id: `${field}:empty`, kind, label: labels.empty(field), field, weight: 0.3, empty: true });

    // ── Colours ──────────────────────────────────────────────────
    // Primary before secondary, and both under one field: they are edited
    // together and a vendor does not think of them as two lists.
    const colors = [...take(k.primaryColors, 'primaryColors'), ...take(k.secondaryColors, 'primaryColors')]
        .slice(0, LIMITS.primaryColors);
    if (colors.length) {
        colors.forEach((hex, i) => nodes.push({
            id: `color:${hex}:${i}`,
            kind: 'color',
            label: hex,
            value: hex,
            field: 'primaryColors',
            // The first few are the brand's; the tail is supporting colour.
            weight: i < 3 ? 1 : 0.7,
        }));
    } else {
        blank('primaryColors', 'color');
    }

    // ── Photography ──────────────────────────────────────────────
    const images = take(k.referenceImageUrls, 'referenceImageUrls');
    if (images.length) {
        images.forEach((url, i) => nodes.push({
            id: `image:${i}`,
            kind: 'image',
            label: '',
            value: url,
            field: 'referenceImageUrls',
            weight: 1,
        }));
    } else {
        blank('referenceImageUrls', 'image');
    }

    // ── Words ────────────────────────────────────────────────────
    const words = take(k.styleKeywords, 'styleKeywords');
    if (words.length) {
        words.forEach((word, i) => nodes.push({
            id: `keyword:${i}`, kind: 'word', label: word, field: 'styleKeywords', weight: 0.8,
        }));
    } else {
        blank('styleKeywords', 'word');
    }

    const banned = take(k.bannedWords, 'bannedWords');
    banned.forEach((word, i) => nodes.push({
        id: `banned:${i}`, kind: 'word', label: word, field: 'bannedWords', weight: 0.5,
    }));

    // ── Sentences ────────────────────────────────────────────────
    // One node each: these are paragraphs, and a sphere is no place to read
    // one. The node says enough to recognise it and opens the rest.
    const sentence = (field: BrandFieldKey, value?: string) => {
        const text = value?.trim();
        if (text) {
            nodes.push({
                id: field, kind: 'text', label: shorten(text), field, weight: 0.85,
                detail: text,
            });
        } else {
            blank(field);
        }
    };
    sentence('toneOfVoice', k.toneOfVoice);
    sentence('visualStyle', k.visualStyle);
    sentence('houseRules', profile.houseRules);

    // ── The typeface, shown in itself where the browser has it ────
    if (k.primaryFont?.trim()) {
        nodes.push({
            id: 'primaryFont', kind: 'font', label: k.primaryFont.trim(),
            value: k.primaryFont.trim(), field: 'primaryFont', weight: 0.9,
        });
    } else {
        blank('primaryFont', 'font');
    }

    // ── The business around the brand ────────────────────────────
    take(profile.competitors, 'competitors').forEach((c, i) => nodes.push({
        id: `competitor:${i}`, kind: 'word', label: c.replace(/^https?:\/\//, ''), field: 'competitors', weight: 0.5,
    }));
    take(profile.markets, 'markets').forEach((m, i) => nodes.push({
        id: `market:${i}`, kind: 'word', label: m, field: 'markets', weight: 0.5,
    }));

    return nodes;
}

/**
 * Whether the kit has anything in it at all.
 *
 * Every field produces a node even when empty, so counting nodes would say
 * "twelve things" about a brand nobody has read yet.
 */
export const hasAnything = (nodes: BrandNode[]): boolean => nodes.some((n) => !n.empty);
